import { Table } from '@cloudflare/kumo';
import type { Key, ReactNode } from 'react';
import { SkeletonTable } from './Skeleton';

export interface DataColumn<T> {
  id: string;
  header: ReactNode;
  cell: (item: T) => ReactNode;
  width?: number | string;
  minWidth?: number | string;
  ariaLabel?: (data: { sorted: boolean; descending: boolean }) => string;
}

interface DataTableProps<T> {
  items: readonly T[];
  columnDefinitions: readonly DataColumn<T>[];
  trackBy?: keyof T | ((item: T) => Key);
  empty?: ReactNode;
  loading?: boolean;
  loadingText?: string;
  header?: ReactNode;
  variant?: string;
  wrapLines?: boolean;
  ariaLabels?: { tableLabel?: string; selectionGroupLabel?: string };
}

/** Tabela simples sobre o `Table` do Kumo, com a API de colunas que as páginas já usavam. */
export function DataTable<T>({ items, columnDefinitions, trackBy, empty, loading, loadingText, header, ariaLabels }: DataTableProps<T>) {
  const keyOf = (item: T, i: number): Key => {
    if (typeof trackBy === 'function') return trackBy(item);
    if (trackBy) return String(item[trackBy]);
    return i;
  };
  return (
    <div className="min-w-0">
      {header}
      <Table aria-label={ariaLabels?.tableLabel}>
        <Table.Header>
          <Table.Row>
            {columnDefinitions.map((c) => (
              <Table.Head key={c.id} style={{ width: c.width, minWidth: c.minWidth }}>{c.header}</Table.Head>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map((item, i) => (
            <Table.Row key={keyOf(item, i)}>
              {columnDefinitions.map((c) => (
                <Table.Cell key={c.id}>{c.cell(item)}</Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {loading ? (
        <SkeletonTable rows={5} cols={Math.max(columnDefinitions.length, 2)} label={loadingText} />
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-kumo-subtle">{empty}</div>
      ) : null}
    </div>
  );
}
